import React, { useEffect, useState } from 'react';
import { CalendarClock, Edit, Mail, Plus, Star, Trash2, UserCheck, Users, X } from 'lucide-react';
import { ConfirmDialog } from '../../../shared/components/ConfirmDialog';
import { Can } from '../../../shared/components/Can';
import { FEATURES } from '../../../shared/hooks/usePermissions';
import {
  useAtualizarContato,
  useContatos,
  useCriarContato,
  useDefinirContatoPrincipal,
  useExcluirContato,
  useResponsabilidadesContato,
} from '../hooks';
import { SubstituicaoContatoModal } from './SubstituicaoContatoModal';
import { AfastamentoContatoModal } from './AfastamentoContatoModal';
import { getFriendlyErrorMessage } from '../../../shared/lib/errors';
import { TextInput } from '../../../shared/components/TextInput';
import { Textarea } from '../../../shared/components/Textarea';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { ContatoComVinculosError, type Contato, type ContactResponsibilityOption, type CreateContatoPayload, type TipoContato, type UpdateContatoPayload } from '../api';
import { maskPhone } from '../../../shared/utils/validators';

interface Props {
  clientId: string;
}

const TIPO_LABEL: Record<TipoContato, string> = {
  comercial: 'Comercial',
  tecnico: 'Técnico',
  financeiro: 'Financeiro',
  juridico: 'Jurídico',
  operacional: 'Operacional',
  outros: 'Outros',
};

/**
 * CRUD de contatos do cliente (SPEC 02 §1 — RN-COM-043, Cenários 8 e 9).
 *
 * Mostra tabela com contatos ativos, botão de novo e ações por linha.
 * Backend garante exatamente 1 principal ativo (índice único parcial)
 * e bloqueia exclusão física de contatos com vínculos operacionais
 * (mostra mensagem de "só é possível inativar").
 */
export const ClienteContatos: React.FC<Props> = ({ clientId }) => {
  const { data, isLoading, isError, error } = useContatos(clientId);
  const criar = useCriarContato(clientId);
  const atualizar = useAtualizarContato(clientId);
  const excluir = useExcluirContato(clientId);
  const definirPrincipal = useDefinirContatoPrincipal(clientId);
  const [modo, setModo] = useState<{ tipo: 'create' } | { tipo: 'edit'; contato: Contato } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Contato | null>(null);
  const [feedback, setFeedback] = useState<{ kind: 'ok' | 'err'; msg: string } | null>(null);
  // SPEC 03 Fase B — modais de substituição + afastamento.
  const [substituindo, setSubstituindo] = useState<Contato | null>(null);
  const [afastando, setAfastando] = useState<Contato | null>(null);

  const contatos: Contato[] = data?.data ?? [];

  const showFeedback = (kind: 'ok' | 'err', msg: string) => {
    setFeedback({ kind, msg });
    setTimeout(() => setFeedback(null), 4000);
  };

  const extractError = (err: unknown): string => getFriendlyErrorMessage(err, 'Falha na operação.');

  const handleCreate = async (payload: CreateContatoPayload) => {
    try {
      await criar.mutateAsync(payload);
      showFeedback('ok', 'Contato adicionado.');
      setModo(null);
    } catch (err) {
      showFeedback('err', extractError(err));
    }
  };

  const handleUpdate = async (contactId: string, payload: UpdateContatoPayload) => {
    try {
      await atualizar.mutateAsync({ contactId, data: payload });
      showFeedback('ok', 'Contato atualizado.');
      setModo(null);
    } catch (err) {
      showFeedback('err', extractError(err));
    }
  };

  const handleSetPrincipal = async (contactId: string) => {
    try {
      await definirPrincipal.mutateAsync(contactId);
      showFeedback('ok', 'Contato principal atualizado.');
    } catch (err) {
      showFeedback('err', extractError(err));
    }
  };

  const performDelete = async () => {
    if (!confirmDelete) return;
    try {
      await excluir.mutateAsync(confirmDelete.id);
      showFeedback('ok', 'Contato removido.');
      setConfirmDelete(null);
    } catch (err) {
      // Cenário 9: contato vinculado só pode ser inativado.
      if (err instanceof ContatoComVinculosError) {
        showFeedback(
          'err',
          'Contato vinculado a operações (contrato/proposta/OS/faturamento). Só é possível inativar.',
        );
      } else {
        showFeedback('err', extractError(err));
      }
      setConfirmDelete(null);
    }
  };

  return (
    <div className="space-y-3" data-testid="cliente-contatos" data-pending-anchor="contatos">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Users size={18} /> Contatos
        </h3>
        <button
          type="button"
          onClick={() => setModo({ tipo: 'create' })}
          className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
          data-testid="btn-novo-contato"
        >
          <Plus size={16} /> Novo contato
        </button>
      </div>

      {feedback && (
        <div
          className={`rounded-md border p-3 text-sm ${
            feedback.kind === 'ok'
              ? 'border-green-200 bg-green-50 text-green-700'
              : 'border-red-200 bg-red-50 text-red-700'
          }`}
          data-testid="contatos-feedback"
        >
          {feedback.msg}
        </div>
      )}

      {isLoading ? (
        <div className="text-sm text-gray-500">Carregando contatos…</div>
      ) : isError ? (
        <div className="text-sm text-red-700">Erro: {(error as Error)?.message}</div>
      ) : contatos.length === 0 ? (
        <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 p-6 text-center text-sm text-gray-500">
          Nenhum contato cadastrado. Ativação exige contato principal (RN-COM-043).
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nome</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Área</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Responsabilidades</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">E-mail</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Telefone</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Principal</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {contatos.map((c) => (
                <tr key={c.id} className={c.isPrincipal ? 'bg-yellow-50/50' : ''} data-testid={`contato-row-${c.id}`}>
                  <td className="px-4 py-2 text-sm text-gray-900">
                    {c.nome}
                    {c.cargo && <div className="text-xs text-gray-500">{c.cargo}</div>}
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-700">{c.area}</td>
                  <td className="px-4 py-2 text-sm text-gray-700">
                    {c.responsibilities && c.responsibilities.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {c.responsibilities.map((r) => (
                          <span
                            key={r.id}
                            className="inline-flex rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-800"
                            data-testid={`resp-badge-${r.code}`}
                            title={r.code}
                          >
                            {r.label}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">
                        {TIPO_LABEL[c.tipoContato] /* fallback legado */}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-700">
                    <a href={`mailto:${c.email}`} className="text-blue-600 hover:underline inline-flex items-center gap-1">
                      <Mail size={12} /> {c.email}
                    </a>
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-700 font-mono">
                    {c.telefone ?? c.celular ?? '—'}
                  </td>
                  <td className="px-4 py-2 text-sm">
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                        c.status === 'ativo'
                          ? 'bg-green-100 text-green-800'
                          : c.status === 'substituido'
                            ? 'bg-orange-100 text-orange-800'
                            : 'bg-gray-100 text-gray-700'
                      }`}
                      data-testid={`contato-status-${c.id}`}
                    >
                      {c.status === 'ativo'
                        ? 'Ativo'
                        : c.status === 'substituido'
                          ? 'Substituído'
                          : 'Inativo'}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-sm">
                    {c.isPrincipal ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800">
                        <Star size={12} /> Principal
                      </span>
                    ) : c.status === 'ativo' ? (
                      <button
                        type="button"
                        onClick={() => handleSetPrincipal(c.id)}
                        disabled={definirPrincipal.isPending}
                        className="text-xs text-blue-600 hover:text-blue-800 hover:underline disabled:opacity-50"
                        data-testid={`btn-set-principal-${c.id}`}
                      >
                        Tornar principal
                      </button>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => setModo({ tipo: 'edit', contato: c })}
                        className="p-1 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded transition-colors"
                        title="Editar contato"
                        aria-label={`Editar contato ${c.nome}`}
                      >
                        <Edit size={16} />
                      </button>
                      {/* SPEC 03 Fase B — só faz sentido em contato ativo. */}
                      {c.status === 'ativo' && (
                        <button
                          type="button"
                          onClick={() => setAfastando(c)}
                          className="p-1 text-yellow-600 hover:text-yellow-900 hover:bg-yellow-50 rounded transition-colors"
                          title="Registrar afastamento"
                          aria-label={`Registrar afastamento ${c.nome}`}
                          data-testid={`btn-afastar-${c.id}`}
                        >
                          <CalendarClock size={16} />
                        </button>
                      )}
                      {c.status === 'ativo' && (
                        <Can feature={FEATURES.clientesContatos} action="create">
                          <button
                            type="button"
                            onClick={() => setSubstituindo(c)}
                            className="p-1 text-purple-600 hover:text-purple-900 hover:bg-purple-50 rounded transition-colors"
                            title="Substituir contato"
                            aria-label={`Substituir contato ${c.nome}`}
                            data-testid={`btn-substituir-${c.id}`}
                          >
                            <UserCheck size={16} />
                          </button>
                        </Can>
                      )}
                      <button
                        type="button"
                        onClick={() => setConfirmDelete(c)}
                        disabled={excluir.isPending}
                        className="p-1 text-red-600 hover:text-red-900 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                        title="Excluir contato"
                        aria-label={`Excluir contato ${c.nome}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modo && (
        <ContatoFormModal
          mode={modo.tipo}
          initial={modo.tipo === 'edit' ? modo.contato : undefined}
          submitting={criar.isPending || atualizar.isPending}
          onCancel={() => setModo(null)}
          onSubmit={async (payload) => {
            if (modo.tipo === 'edit') {
              // Em edit, ignora isPrincipal — endpoint dedicado cuida.
              const { isPrincipal: _p, ...rest } = payload;
              await handleUpdate(modo.contato.id, rest);
            } else {
              await handleCreate(payload);
            }
          }}
        />
      )}

      <ConfirmDialog
        open={!!confirmDelete}
        title="Excluir contato?"
        message={
          confirmDelete
            ? `${confirmDelete.nome} (${TIPO_LABEL[confirmDelete.tipoContato]})\n${confirmDelete.email}${
                confirmDelete.isPrincipal
                  ? '\n\nEste é o contato PRINCIPAL. Ao excluir, o próximo contato ativo (por nome) será promovido automaticamente.'
                  : ''
              }\n\nSe o contato estiver vinculado a contrato/proposta/OS/faturamento, apenas a inativação é permitida.`
            : ''
        }
        confirmLabel="Excluir"
        danger
        busy={excluir.isPending}
        onConfirm={performDelete}
        onCancel={() => setConfirmDelete(null)}
      />

      {/* SPEC 03 Fase B — modais de substituição + afastamento. Só um
          pode estar aberto por vez; ambos precisam de candidatos
          ativos diferentes do contato-alvo. */}
      {substituindo && (
        <SubstituicaoContatoModal
          clientId={clientId}
          oldContact={substituindo}
          candidates={contatos.filter(
            (c) => c.id !== substituindo.id && c.status === 'ativo',
          )}
          onClose={() => setSubstituindo(null)}
          onDone={() => showFeedback('ok', 'Contato substituído com sucesso.')}
        />
      )}
      {afastando && (
        <AfastamentoContatoModal
          clientId={clientId}
          contact={afastando}
          candidates={contatos.filter(
            (c) => c.id !== afastando.id && c.status === 'ativo',
          )}
          onClose={() => setAfastando(null)}
          onDone={() => showFeedback('ok', 'Afastamento registrado.')}
        />
      )}
    </div>
  );
};

interface ContatoFormModalProps {
  mode: 'create' | 'edit';
  initial?: Contato;
  submitting: boolean;
  onCancel: () => void;
  onSubmit: (payload: CreateContatoPayload) => Promise<void> | void;
}

const ContatoFormModal: React.FC<ContatoFormModalProps> = ({
  mode,
  initial,
  submitting,
  onCancel,
  onSubmit,
}) => {
  const [nome, setNome] = useState('');
  const [cargo, setCargo] = useState('');
  const [area, setArea] = useState('');
  const [email, setEmail] = useState('');
  const [telefone, setTelefone] = useState('');
  const [celular, setCelular] = useState('');
  const [isPrincipal, setIsPrincipal] = useState(false);
  const [receivesCommunications, setReceivesCommunications] = useState(true);
  const [observacoes, setObservacoes] = useState('');
  const [error, setError] = useState<string | null>(null);
  // SPEC 03 — checklist de responsabilidades substitui o select único.
  const [selectedResponsibilities, setSelectedResponsibilities] = useState<Set<string>>(new Set());
  // SPEC 03 Fase B/C — campos adicionais de contato.
  const [empresaUnidade, setEmpresaUnidade] = useState('');
  const [tipoVinculo, setTipoVinculo] = useState('');
  const [canaisPref, setCanaisPref] = useState<Set<string>>(new Set(['email']));
  const [horarioPref, setHorarioPref] = useState('');
  const [optOutRaw, setOptOutRaw] = useState('');
  const { data: responsabilidadesData, isLoading: loadingResp } = useResponsabilidadesContato();
  const responsabilidades: ContactResponsibilityOption[] = responsabilidadesData?.data ?? [];

  useEffect(() => {
    if (initial) {
      setNome(initial.nome);
      setCargo(initial.cargo ?? '');
      setArea(initial.area);
      setEmail(initial.email);
      setTelefone(initial.telefone ?? '');
      setCelular(initial.celular ?? '');
      setIsPrincipal(initial.isPrincipal);
      setReceivesCommunications(initial.receivesCommunications);
      setObservacoes(initial.observacoes ?? '');
      setSelectedResponsibilities(
        new Set((initial.responsibilities ?? []).map((r) => r.id)),
      );
      setEmpresaUnidade(initial.empresaUnidade ?? '');
      setTipoVinculo(initial.tipoVinculo ?? '');
      // SPEC 03 Fase C — hidrata preferências existentes.
      const pref = initial.preferenciasComunicacao as
        | { canais?: string[]; horario?: string; optOut?: string[] }
        | null
        | undefined;
      if (pref) {
        setCanaisPref(new Set(pref.canais ?? ['email']));
        setHorarioPref(pref.horario ?? '');
        setOptOutRaw((pref.optOut ?? []).join('\n'));
      }
      setError(null);
    }
  }, [initial]);

  const toggleResponsibility = (id: string) => {
    setSelectedResponsibilities((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const isEdit = mode === 'edit';

  const handleSubmit = async (evt: React.FormEvent) => {
    evt.preventDefault();
    setError(null);
    const partes = nome.trim().split(/\s+/).filter(Boolean);
    if (partes.length < 2) {
      setError('Nome completo deve incluir pelo menos nome e sobrenome.');
      return;
    }
    if (!area.trim()) {
      setError('Área/Departamento é obrigatória.');
      return;
    }
    // Validação básica de e-mail — backend também valida.
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError('E-mail em formato inválido.');
      return;
    }
    // SPEC 03 — pelo menos uma responsabilidade obrigatória.
    if (selectedResponsibilities.size === 0) {
      setError('Selecione ao menos uma responsabilidade.');
      return;
    }
    const payload: CreateContatoPayload = {
      nome: nome.trim(),
      area: area.trim(),
      email: email.trim().toLowerCase(),
      responsibilityIds: Array.from(selectedResponsibilities),
      receivesCommunications,
    };
    if (cargo.trim()) payload.cargo = cargo.trim();
    if (telefone.trim()) payload.telefone = telefone.trim();
    if (celular.trim()) payload.celular = celular.trim();
    if (observacoes.trim()) payload.observacoes = observacoes.trim();
    if (!isEdit && isPrincipal) payload.isPrincipal = true;
    // SPEC 03 Fase B/C — envia campos novos só se algo foi preenchido.
    if (empresaUnidade.trim()) payload.empresaUnidade = empresaUnidade.trim();
    if (tipoVinculo) {
      payload.tipoVinculo = tipoVinculo as CreateContatoPayload['tipoVinculo'];
    }
    const optOut = optOutRaw.split('\n').map((s) => s.trim()).filter(Boolean);
    const pref: Record<string, unknown> = {};
    if (canaisPref.size > 0) pref.canais = Array.from(canaisPref);
    if (horarioPref.trim()) pref.horario = horarioPref.trim();
    if (optOut.length > 0) pref.optOut = optOut;
    if (Object.keys(pref).length > 0) {
      payload.preferenciasComunicacao = pref;
    }
    await onSubmit(payload);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 p-4">
          <h3 className="text-xl font-bold text-gray-900">
            {isEdit ? 'Editar contato' : 'Novo contato'}
          </h3>
          <button type="button" onClick={onCancel} aria-label="Fechar" className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3 p-6" data-testid="contato-form">
          <TextInput
            label="Nome completo *"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            data-testid="contato-input-nome"
          />

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <TextInput
              label="Cargo"
              value={cargo}
              onChange={(e) => setCargo(e.target.value)}
            />
            <TextInput
              label="Área / Departamento *"
              value={area}
              onChange={(e) => setArea(e.target.value)}
              data-testid="contato-input-area"
            />
          </div>

          {/* SPEC 03 — checklist de responsabilidades. Substitui o
              select único; a mesma pessoa pode ser Gestor + Aprovador
              + Operacional simultaneamente (RN US-COM-003 §3). */}
          <fieldset className="rounded-md border border-gray-200 p-3">
            <legend className="px-1 text-sm font-medium text-gray-700">
              Responsabilidades *
            </legend>
            {loadingResp ? (
              <div className="text-xs text-gray-500">Carregando responsabilidades…</div>
            ) : (
              <div className="grid grid-cols-1 gap-1 md:grid-cols-2" data-testid="responsibilities-checklist">
                {responsabilidades.map((r) => (
                  <label
                    key={r.id}
                    className="flex items-center gap-2 text-sm"
                    data-testid={`resp-check-${r.code}`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedResponsibilities.has(r.id)}
                      onChange={() => toggleResponsibility(r.id)}
                    />
                    <span>{r.label}</span>
                  </label>
                ))}
              </div>
            )}
            <p className="mt-2 text-xs text-gray-500">
              Selecione pelo menos uma. Uma mesma pessoa pode ter várias responsabilidades.
            </p>
          </fieldset>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {/* placeholder de linha para manter o grid abaixo */}
            <div className="hidden md:block" />
            {/*
              Usamos type="text" (não "email") para que a validação
              fique no `handleSubmit` e produza mensagem consistente
              no `contato-form-error`. HTML5 email validation bloquearia
              o submit antes do handler rodar, sem UX unificada.
            */}
            <TextInput
              label="E-mail *"
              type="text"
              inputMode="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="contato-input-email"
            />
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <TextInput
              label="Telefone (com DDD)"
              inputMode="tel"
              value={telefone}
              onChange={(e) => setTelefone(maskPhone(e.target.value))}
            />
            <TextInput
              label="Celular / WhatsApp"
              inputMode="tel"
              value={celular}
              onChange={(e) => setCelular(maskPhone(e.target.value))}
            />
          </div>

          <Textarea
            label="Observações"
            rows={2}
            value={observacoes}
            onChange={(e) => setObservacoes(e.target.value)}
          />

          {/* SPEC 03 Fase B — empresa/unidade + tipo de vínculo */}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <TextInput
              label="Empresa / Unidade"
              value={empresaUnidade}
              onChange={(e) => setEmpresaUnidade(e.target.value)}
              data-testid="contato-empresa-unidade"
            />
            <AutocompleteSelect
              label="Tipo de vínculo"
              options={[
                { value: '', label: '— não informado —' },
                { value: 'empregado', label: 'Empregado' },
                { value: 'terceiro', label: 'Terceiro' },
                { value: 'consultor', label: 'Consultor' },
                { value: 'representante', label: 'Representante' },
              ]}
              value={tipoVinculo}
              onChange={(v) => setTipoVinculo(v)}
            />
          </div>

          {/* SPEC 03 Fase C — preferências de comunicação */}
          <fieldset className="rounded-md border border-gray-200 p-3">
            <legend className="px-1 text-sm font-medium text-gray-700">
              Preferências de comunicação
            </legend>
            <div className="mb-2">
              <span className="mb-1 block text-xs text-gray-500">Canais preferidos</span>
              <div className="flex flex-wrap gap-3">
                {(['email', 'telefone', 'whatsapp'] as const).map((canal) => (
                  <label key={canal} className="flex items-center gap-1 text-sm">
                    <input
                      type="checkbox"
                      checked={canaisPref.has(canal)}
                      onChange={() => {
                        setCanaisPref((prev) => {
                          const next = new Set(prev);
                          if (next.has(canal)) next.delete(canal);
                          else next.add(canal);
                          return next;
                        });
                      }}
                      data-testid={`canal-${canal}`}
                    />
                    <span className="capitalize">{canal}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="mb-2">
              <TextInput
                label="Horário preferido"
                value={horarioPref}
                onChange={(e) => setHorarioPref(e.target.value)}
                data-testid="pref-horario"
              />
            </div>
            <div>
              <span className="mb-1 block text-xs text-gray-500">
                Opt-out por tipo de notificação (uma por linha)
              </span>
              <Textarea
                label="Opt-out"
                rows={2}
                value={optOutRaw}
                onChange={(e) => setOptOutRaw(e.target.value)}
                data-testid="pref-optout"
              />
            </div>
          </fieldset>

          <div className="flex flex-col gap-2 sm:flex-row sm:gap-6">
            {!isEdit && (
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={isPrincipal}
                  onChange={(e) => setIsPrincipal(e.target.checked)}
                  data-testid="contato-checkbox-principal"
                />
                Definir como contato principal
              </label>
            )}
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={receivesCommunications}
                onChange={(e) => setReceivesCommunications(e.target.checked)}
              />
              Recebe comunicações
            </label>
          </div>
          {isEdit && initial?.isPrincipal && (
            <div className="rounded-md border border-yellow-200 bg-yellow-50 p-2 text-xs text-yellow-800">
              Este é o contato principal. Para trocar, use "Tornar principal" em outro contato.
            </div>
          )}

          {error && (
            <div
              className="rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-700"
              data-testid="contato-form-error"
            >
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onCancel}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              data-testid="contato-btn-salvar"
            >
              {submitting ? 'Salvando…' : isEdit ? 'Salvar alterações' : 'Adicionar contato'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
